import { Fragment, useContext, useEffect, useState } from 'react';
import { Location, To, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, Divider, Drawer, ListItemIcon, ListItemText, MenuItem, MenuList } from '@mui/material';
import { KeyboardArrowRight } from '@mui/icons-material';

import { MenuHeader } from '@/components/home/MenuCloneIcons';
import { chatContext } from '@/contexts/ChatContext';
import { ctxUser } from '@/contexts/UserContext';
import { getAdditionalElements } from '@/popups';
import { type PopupType } from '@/types';
import { NavigationIcon } from '@/shared/ui/navigation-icon/NavigationIcon';
import { absolutePopupPath, joinPopupPath } from '@/utilities/popupPath';
import { filterVisiblePopups } from '@/components/home/popupVisibility';

export default function More({
  closePopup,
  hasTachograph,
}: {
  closePopup: () => Promise<void>;
  hasTachograph?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const chatCtx = useContext(chatContext);
  const userCtx = useContext(ctxUser);
  const { t } = useTranslation();
  const addElementsList = getAdditionalElements(closePopup, true, hasTachograph);
  const [open, setOpen] = useState(false);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState<number | null>(null);

  const handleToggleSubmenu = (index: number) => {
    setOpenSubmenuIndex((prev) => (prev === index ? null : index));
  };

  function onClose() {
    // Azert -1, mert a getNav fuggveny (aktiv && More) sorában -1-et ad vissza
    void navigate(-1 as To, location);
  }

  // ez ugyanaz mint a LeftNav-nal ??
  function getNav(p: PopupType, location: Location) {
    if (p.level === 1) return p.navigateTo || absolutePopupPath(p.path);

    // ha en vagyok az aktiv es a more vagyok :( ez sajnos most be van egetve
    if (p.navigateTo && location.pathname.endsWith(p.navigateTo)) return -1;

    // ha en vagyok az aktiv akkor nem csinal semmit
    if (location.pathname.endsWith(p.path)) return location.pathname;

    // nem en vagyok az aktiv
    // mi van ha az elso resz nem a tracking vagy a route ?? ha a reports-rol jon ???
    const parts = location.pathname.split('/'); // regex lehet jobb lenne
    if (parts.length === 2) {
      // a nulladik elem ures string :(
      return joinPopupPath(location.pathname, p.path);
    }
    return joinPopupPath('/' + parts[1], p.path);
  }

  useEffect(() => {
    setTimeout(() => {
      setOpen(true);
    });
  }, []);

  const elements = filterVisiblePopups(addElementsList, {
    user: userCtx?.user,
    chatId: chatCtx?.chatId,
    platform: 'mobile',
    isTokenUser: userCtx?.isToken() ?? false,
    hasExtraFeature: (extraFeature) => userCtx?.hasExtraFeature(extraFeature) ?? false,
  });

  return (
    <Drawer
      variant="temporary"
      disableEnforceFocus
      disablePortal
      anchor="bottom"
      transitionDuration={0}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: false,
        onClose,
      }}
      slotProps={{
        root: { sx: { zIndex: 1301 } }, // butan nez ki ahogy animal, lehetne megint magas de hatrebb
        paper: {
          id: 'more_menu_drawer',
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            bottom: 'var(--bottom-nav-bar-height)',
            overflowY: 'auto',
          },
        },
      }}
    >
      <Box>
        <MenuHeader drawerMenuOpen={true} isBottom={true} />
        <Divider />
      </Box>
      <MenuList>
        {elements.map((e, index) => {
          const hasChildren = Array.isArray(e.children) && e.children.length > 0;
          const isOpen = openSubmenuIndex === index;
          const handleMenuItemClick = () => {
            if (hasChildren) {
              handleToggleSubmenu(index);
              return;
            }

            if (e.element) {
              void navigate(getNav(e, location) as To, { replace: true });
              return;
            }

            void navigate('/logout');
          };

          return (
            <Fragment key={e.title}>
              <MenuItem onClick={handleMenuItemClick} data-qa-element-id={e.dataQaElementId}>
                <ListItemIcon>
                  <NavigationIcon popup={e} />
                </ListItemIcon>
                <ListItemText data-qa-element-id={`menu_item_text_${e.title}`}>{t(e.title!)}</ListItemText>

                {hasChildren && (
                  <KeyboardArrowRight
                    sx={{
                      ml: 'auto',
                      transition: 'transform 0.2s ease',
                      transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  />
                )}
              </MenuItem>
              {hasChildren && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <MenuList sx={{ pl: 4 }} data-qa-element-id={`submenu_for_${e.dataQaElementId}`}>
                    {e.children?.map((child) => {
                      const handleChildClick = () => {
                        if (child.element) {
                          void navigate(getNav(child, location) as To, { replace: true });
                          return;
                        }

                        void navigate('/logout');
                      };

                      return (
                        <MenuItem
                          key={child.title}
                          onClick={handleChildClick}
                          data-qa-element-id={`submenu_${child.dataQaElementId}`}
                        >
                          <ListItemIcon>
                            <NavigationIcon popup={child} />
                          </ListItemIcon>
                          <ListItemText data-qa-element-id={`menu_item_text_${e.title}`}>
                            {t(child.title!)}
                          </ListItemText>
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Collapse>
              )}
            </Fragment>
          );
        })}
      </MenuList>
    </Drawer>
  );
}
